import {
  Grid,
  Skeleton,
  useMediaQuery,
  useTheme,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import carPlaceholder from '../../../public/assets/CarPlaceholder.png';
import { assignTextOnLoading } from '../../helpers/orders';
import { isReconditioning } from '../../helpers/reconditioning';
import EditButton from '../EditButton';
import GenDataText from '../GenDataText';
import { checkPhotoCountEnabled } from './WorkOrderHelper';

const WOrderVehicleInfo = ({
  carImage = null,
  isLoading,
  apiLoading = false,
  data,
  onClickEdit,
  photoCount,
}) => {
  const { t } = useTranslation('workOrdersTable');
  const theme = useTheme();
  const largeScreen = useMediaQuery(theme.breakpoints.up('lg'));
  const isBelowSmScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const textDirection = () => (largeScreen ? 'vertical' : null);

  const wOrderCarData = [
    {
      label: t('detail.vehicleData.vinLabel'),
      text: assignTextOnLoading(data?.vehicle?.vin, isLoading),
      order: { xs: 1, sm: 1, lg: 1 },
      ellipsis: { direction: 'left', size: 77 },
    },
    {
      label: t('detail.vehicleData.makeLabel'),
      text: assignTextOnLoading(data?.vehicle?.make, isLoading),
      order: { xs: 5, sm: 5, lg: 2 },
    },
    {
      label: t('detail.vehicleData.colorLabel'),
      text: assignTextOnLoading(data?.vehicle?.color, isLoading),
      order: { xs: 4, sm: 4, lg: 7 },
    },
    {
      label: t('detail.vehicleData.modelLabel'),
      text: assignTextOnLoading(data?.vehicle?.model, isLoading),
      order: { xs: 7, sm: 7, lg: 6 },
      ellipsis: { direction: 'right', size: 75 },
    },
    {
      label: t('detail.vehicleData.yearLabel'),
      text: assignTextOnLoading(data?.vehicle?.year, isLoading),
      order: { xs: 2, sm: 2, lg: 3 },
    },
    {
      label: t('detail.vehicleData.stockLabel'),
      text: assignTextOnLoading(data?.stockNumber, isLoading),
      order: { xs: 3, sm: 3, lg: 5 },
      ellipsis: { direction: 'right', size: 66 },
    },
    {
      label: t('detail.vehicleData.pOLabel'),
      text: assignTextOnLoading(data?.po, isLoading),
      order: { xs: 8, sm: 8, lg: 8 },
    },
    {
      label: t('detail.vehicleData.rOLabel'),
      text: assignTextOnLoading(data?.ro, isLoading),
      order: { xs: 6, sm: 6, lg: 4 },
    },
  ];

  data &&
    isReconditioning(data) &&
    wOrderCarData?.splice(
      wOrderCarData.findIndex(o => o.label === t('detail.vehicleData.pOLabel')),
      1
    );

  const handleEllipsis = (direction, size) =>
    direction === 'left'
      ? {
          width: `${size}px`,
          display: 'inline-block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          direction: 'rtl',
          textAlign: 'left',
        }
      : {
          width: `${size}px`,
          display: 'inline-block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          direction: 'ltr',
          textAlign: 'left',
        };

  const carImageHandler = () =>
    !isLoading ? (
      <Box
        component="img"
        src={carImage ? carImage : carPlaceholder}
        sx={{
          borderRadius: 1,
          border: 1,
          borderColor: theme.palette.grey[100],
          width: '100%',
        }}
      />
    ) : (
      <Skeleton
        data-testid="skeleton"
        variant="rounded"
        sx={{ width: '100%' }}
        height={190}
      />
    );

  return (
    <Box
      sx={{
        bgcolor: theme.palette.background.paper,
        p: { xs: 2, lg: 4 },
        borderTop: '1px solid',
        borderColor: theme.palette.divider,
      }}
    >
      <Grid container flexDirection="row" sx={{ position: 'relative' }}>
        <Grid
          item
          sx={{
            width: { lg: '17%', md: '20%', sm: '40%', xs: '100%' },
            marginRight: { sm: 2 },
            marginBottom: { xs: 1, sm: 0 },
          }}
        >
          {carImageHandler()}
        </Grid>
        <Grid
          item
          container
          direction="column"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'baseline',
            width: {
              sm: `calc(60% - ${theme.spacing(2)})`,
              md: `calc(65% - ${theme.spacing(2)})`,
            },
            maxWidth: '775px',
          }}
        >
          <Grid
            container
            alignItems="center"
            spacing={2}
            sx={{ width: '100%', height: { md: '100%' } }}
          >
            {wOrderCarData.map((d, i) => (
              <Grid
                item
                key={i}
                sx={{
                  order: { xs: d?.order.xs, sm: d?.order.sm, lg: d?.order.lg },
                  width: { xs: '50%', md: '30%', lg: '25%' },
                  mb: { xs: 1, md: 0 },
                }}
              >
                <GenDataText
                  label={d.label}
                  text={d.text}
                  alignItems="start"
                  direction={textDirection()}
                  style={
                    isBelowSmScreen && d?.ellipsis
                      ? handleEllipsis(d.ellipsis.direction, d.ellipsis.size)
                      : null
                  }
                />
              </Grid>
            ))}
          </Grid>
          {(apiLoading || photoCount !== null) && (
            <>
              {isLoading && (
                <Skeleton data-testid="skeleton" variant="rounded" />
              )}
              {!isLoading && (
                <Grid
                  item
                  sx={{
                    order: { xs: 5, sm: 5, lg: 5 },
                    width: { xs: '50%', md: '30%', lg: '26%' },
                    mb: { xs: 1, md: 0 },
                  }}
                >
                  {checkPhotoCountEnabled(data?.services) && (
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Box sx={{ width: '100px' }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Photo Count:
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(0, 0, 0, 0.6)' }}
                      >
                        {apiLoading ? (
                          <CircularProgress
                            size={16}
                            color="inherit"
                            data-testid="photocount-loader"
                          />
                        ) : (
                          photoCount
                        )}
                      </Typography>
                    </Box>
                  )}
                </Grid>
              )}
            </>
          )}
        </Grid>
        <Grid
          item
          sx={{
            position: 'absolute',
            top: { lg: 0 },
            bottom: { xs: 0 },
            right: 0,
          }}
        >
          {!isLoading ? (
            <EditButton onClickEdit={onClickEdit} />
          ) : (
            <Skeleton
              variant="rounded"
              sx={{ width: { xs: 40, sm: 180 } }}
              height={40}
            />
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default WOrderVehicleInfo;
